'use client';

import { useState, useEffect } from 'react';
import { Session } from 'next-auth';
import { Button } from '@/components/ui/button';
import { MessageSquareIcon, Clock, ArrowRight, TrendingUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import Link from 'next/link';
import { createChatWithPrompt } from '../actions';
import { useRouter } from 'next/navigation';

interface Chat {
	id: string;
	title: string;
	createdAt: string;
	lastMessageAt: string;
	messageCount: number;
	documentCount: number;
	preview: string;
	status: 'active' | 'completed' | 'archived';
}

interface LoggedInHomeProps {
	session: Session;
}

export function LoggedInHome({ session }: LoggedInHomeProps) {
	const [chats, setChats] = useState<Chat[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [inputValue, setInputValue] = useState('');
	const [isAnalyzing, setIsAnalyzing] = useState(false);
	const router = useRouter();

	useEffect(() => {
		const fetchChats = async () => {
			try {
				const siteId = session?.siteId;
				if (!siteId) {
					console.error('No siteId found in session');
					setIsLoading(false);
					return;
				}

				const response = await fetch(`/api/sites/${siteId}/noslead/chats`);
				if (!response.ok) {
					throw new Error('Failed to fetch chats');
				}
				
				const data = await response.json();
				setChats(data.chats || []);
				setIsLoading(false);
			} catch (error) {
				console.error('Failed to fetch chats:', error);
				setIsLoading(false);
			}
		};

		fetchChats();
	}, [session]);

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
		
		if (diffInHours < 24) {
			return `${diffInHours}h ago`;
		} else if (diffInHours < 168) { // 7 days
			return `${Math.floor(diffInHours / 24)}d ago`;
		} else {
			return date.toLocaleDateString();
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case 'active': return 'bg-green-100 text-green-800 border-green-200';
			case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
			case 'archived': return 'bg-gray-100 text-gray-800 border-gray-200';
			default: return 'bg-gray-100 text-gray-800 border-gray-200';
		}
	};

	const handleSubmit = async () => {
		if (!inputValue.trim()) return;

		console.log('Starting research for:', inputValue);
		setIsAnalyzing(true);

		try {
			// Get siteId from session
			const siteId = session?.siteId || 'default-site';

			// Create chat with the initial prompt
			const result = await createChatWithPrompt(siteId, inputValue);

			// Navigate to chat page
			router.push(result.url);
		} catch (error) {
			console.error('Failed to create chat:', error);
			setIsAnalyzing(false);
		}
	};

	return (
		<div className="min-h-screen bg-gray-50">
			<div className="container mx-auto px-4 py-8">
				{/* Header */}
				<div className="mb-8">
					<h1 className="text-2xl font-semibold text-gray-900">
						Welcome back, {session.user?.name || session.user?.email?.split('@')[0]}
					</h1>
					<p className="text-gray-600 mt-1">
						Continue your research or start a new conversation
					</p>
				</div>

				{/* Quick Start Section */}
				<div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
					<div className="flex gap-3">
						<Input
							type="text"
							placeholder="What kind of companies or people are you looking for?"
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
							className="flex-1 h-12 border-gray-300 focus:border-blue-500 transition-colors"
							disabled={isAnalyzing}
						/>
						<Button
							onClick={handleSubmit}
							disabled={isAnalyzing || !inputValue.trim()}
							className="h-12 px-6 bg-blue-600 hover:bg-blue-700 transition-colors"
						>
							{isAnalyzing ? (
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
							) : (
								<>
									<TrendingUp className="h-4 w-4 mr-2" />
									Research
								</>
							)}
						</Button>
					</div>
				</div>

				{/* Recent Conversations */}
				<div>
					<h2 className="text-lg font-medium text-gray-900 mb-4">Recent Research</h2>

					{isLoading ? (
						<div className="space-y-4">
							{[1, 2, 3].map((i) => (
								<div key={i} className="bg-white p-4 rounded-lg border border-gray-200 animate-pulse">
									<div className="h-4 bg-gray-200 rounded mb-2 w-1/3"></div>
									<div className="h-3 bg-gray-200 rounded mb-2 w-2/3"></div>
									<div className="h-3 bg-gray-200 rounded w-1/4"></div>
								</div>
							))}
						</div>
					) : chats.length > 0 ? (
						<div className="space-y-4">
							{chats.map((chat) => (
								<Link key={chat.id} href={`/noslead/chat/${chat.id}`} className="block">
									<div className="group bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer">
										<div className="flex items-start justify-between mb-2">
											<h3 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
												{chat.title}
											</h3>
											<span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(chat.status)}`}>
												{chat.status}
											</span>
										</div>
										
										<p className="text-sm text-gray-600 mb-3 line-clamp-2">
											{chat.preview}
										</p>

										<div className="flex items-center justify-between">
											<div className="flex items-center gap-4 text-xs text-gray-500">
												<div className="flex items-center gap-1">
													<Clock className="w-3 h-3" />
													{formatDate(chat.lastMessageAt)}
												</div>
												<span>{chat.messageCount} messages</span>
												{chat.documentCount > 0 && (
													<span className="text-blue-600 font-medium">
														{chat.documentCount} lead documents
													</span>
												)}
											</div>

											<ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all duration-200" />
										</div>
									</div>
								</Link>
							))}
						</div>
					) : (
						<div className="text-center py-16 bg-white rounded-lg border border-gray-200">
							<div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
								<MessageSquareIcon className="w-6 h-6 text-gray-400" />
							</div>
							<h3 className="text-lg font-medium text-gray-900 mb-2">No research yet</h3>
							<p className="text-gray-600">Start your first research project above</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}